"use client";

import { c as _c } from "react/compiler-runtime";
import { useQueryClient } from "@tanstack/react-query";
import { useWebSocketCallbackSubscription } from "@/hooks/use-websocket-subscription";
import type { AlertDetail } from "@/types/alert";
import { AlertType } from "@/types/enums/alert-type";
import { noteQueryKeys } from "@/api/provider/scribe/note-query";
import { useScribeStore } from "@/stores/scribe-store";

/**
 * Subscribe to NOTE_UPDATED alerts that target the open telehealth note
 * and invalidate the note detail query so the playground refetches the
 * latest transcript text.
 *
 * The Recall bridge publishes NOTE_UPDATED after each 30 s chunk ingest
 * (and once more after writing recall_bot_ended_at), so the frontend
 * doesn't need its own polling loop.
 */
export function useTelehealthTranscriptStream(noteId, t0) {
  const $ = _c(4);
  const enabled = t0 === undefined ? true : t0;
  const queryClient = useQueryClient();
  let t1;
  if ($[0] !== noteId || $[1] !== queryClient) {
    t1 = function handleMessage(message) {
      if (!noteId) {
        return;
      }
      if (message.type !== AlertType.NOTE_UPDATED) {
        return;
      }
      const data = message.data as {
        data?: {
          note_id?: string;
        };
      };
      const alertNoteId = data?.data?.note_id;
      if (alertNoteId !== noteId) {
        return;
      }
      queryClient.invalidateQueries({
        queryKey: noteQueryKeys.detail(noteId)
      });
      if (useScribeStore.getState().isProcessing) {
        useScribeStore.setState({
          isProcessing: false
        });
      }
    };
    $[0] = noteId;
    $[1] = queryClient;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  const handleMessage = t1;
  let t2;
  if ($[3] === Symbol.for("react.memo_cache_sentinel")) {
    t2 = [AlertType.NOTE_UPDATED];
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  useWebSocketCallbackSubscription(t2, handleMessage, enabled && !!noteId, "TelehealthTranscriptStream");
}